import { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import api from 'api';
import type { MyAccountPayload } from 'api/models/payload';
import { setEnabledCountryCodes, setEnabledFeatures } from 'redux/user/user-slice';
import type { MyAccountInterface } from 'types';
import { normalizeEnabledCountryCodes } from 'utils/countries';
import { normalizeEnabledFeatures } from 'utils/features';

/* ═══════════════════════════════════════════════════════
   Profile picture — single source of truth for current user
   GET/POST /resource/profile_picture
   ═══════════════════════════════════════════════════════ */

export const profilePictureKeys = {
    all: ['profilePicture'] as const,
};

export const fetchProfilePictureUrl = async (): Promise<string> => {
    try {
        const resPic = await api.resource.get('profile_picture');
        return (resPic.data?.url as string) ?? '';
    } catch {
        // No picture (or fetch failure) — empty string, same as historical behavior
        return '';
    }
};

/**
 * Current user's profile picture URL.
 * Shared cache for Header, ProfileAvatar, My Account, etc.
 */
export const useProfilePicture = (options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: profilePictureKeys.all,
        queryFn: fetchProfilePictureUrl,
        staleTime: 5 * 60 * 1000,
        enabled: options?.enabled ?? true,
    });
};

export const useUpdateProfilePicture = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('profile_picture', file);
            await api.resource.post('profile_picture', formData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: profilePictureKeys.all });
        },
    });
};

/* ═══════════════════════════════════════════════════════
   My Account
   ═══════════════════════════════════════════════════════ */

export const useMyAccount = (enabled = true) => {
    const dispatch = useDispatch();
    const [data, setData] = useState<MyAccountInterface | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    const fetchMyAccount = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.myAccount.get();
            setData(response.data);
            dispatch(setEnabledFeatures(normalizeEnabledFeatures(response.data?.enabled_features)));
            dispatch(
                setEnabledCountryCodes(
                    normalizeEnabledCountryCodes(response.data?.enabled_country_codes)
                )
            );
        } catch (error) {
            console.error('Failed to fetch my account:', error);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (enabled) {
            fetchMyAccount();
        }
    }, [enabled, fetchMyAccount]);

    return { data, isLoading, refetch: fetchMyAccount };
};

export const useUpdateMyAccount = () => {
    const [isPending, setIsPending] = useState(false);

    const mutateAsync = async (payload: Partial<MyAccountPayload>) => {
        setIsPending(true);
        try {
            const response = await api.myAccount.put(payload as MyAccountPayload);
            return response.data;
        } finally {
            setIsPending(false);
        }
    };

    return { mutateAsync, isPending };
};
